/**
 * Page Order (task 071-01) — Chelsie's 2026-09-28 ask: "how can I see which
 * sequences and spots each page are in without going into each campaign?"
 *
 * One list of every custom page by sequence and spot. The api
 * (`GET /admin/page-order`) sends the rows in the order visitors see each
 * sequence (epic 077 D10), each page's 7-day responses, revenue and revenue
 * per response, the sequences each audience walks, and the walk's master
 * switch. This screen only groups and labels them: it never re-sorts, except
 * to hand the editor the fallback (spot) order. It has two edit controls:
 * Pin to #1 / Unpin per page (task 077-03), and task 071-03's Change order,
 * which swaps one sequence card's table for the drag-and-drop editor
 * (`page-order-reorder.tsx`). Moving a page to another sequence, or placing a
 * new one, is still done on the campaign form.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type PageOrderResponse, type PageOrderRow } from '../lib/api';
import { campaignStyleEnumToLabel } from '../lib/campaign-styles';
import { fmtCount, fmtDate, fmtMoney } from '../lib/format';
import { Banner } from '../components/ui/banner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { StatusPill } from '../components/ui/status-pill';
import { Tag } from '../components/ui/tag';
import { useToast } from '../components/ui/toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { SequenceReorder, isEndedPage } from './page-order-reorder';

type Group =
  | { kind: 'sequence'; sequence: number; rows: PageOrderRow[] }
  | { kind: 'not-placed'; rows: PageOrderRow[] };

const NULL_SPOT_HINT =
  'Reached only when no numbered page in this sequence is live, and the walk ends after it.';

const REVENUE_HELP =
  'This order seats new pages (under 100 responses) and is the order visitors see if revenue order is switched off. Pin a page to put it at #1.';

const SPOT_MODE_BANNER =
  'Revenue order is off (OFFERS_WALK_ORDER=spot): visitors see pages by spot number and the pin has no effect. The numbers show what the ranking would use.';

function ascending(a: number, b: number): number {
  return a - b;
}

function compareIds(a: string, b: string): number {
  const idA = BigInt(a);
  const idB = BigInt(b);
  if (idA === idB) return 0;
  return idA < idB ? -1 : 1;
}

/**
 * 071's order: spot ascending, no spot last, then campaign id. In revenue
 * mode the API sends the walk order, so the editor is handed this one: it
 * seeds its draft from the order it is given, and saving the walk order back
 * as spots would silently rewrite them.
 */
function bySpot(a: PageOrderRow, b: PageOrderRow): number {
  if (a.spot !== b.spot) {
    if (a.spot === null) return 1;
    if (b.spot === null) return -1;
    return a.spot - b.spot;
  }
  return compareIds(a.campaignId, b.campaignId);
}

/** The calendar date, `yyyy-MM-dd`, in the browser's zone. */
function localYmd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The ranking is stale when its day is before yesterday (the browser's date; ET is close enough). */
function isStale(rankedOn: string): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return rankedOn < localYmd(yesterday);
}

/**
 * Walked sequences first (even with no rows), then every other sequence that
 * holds a row, then Not placed (only when it has rows). Each group keeps the
 * API's row order.
 */
function buildGroups(data: PageOrderResponse): Group[] {
  const walked = [...new Set([data.paidSocialSequence, data.ctoSequence])].sort(ascending);
  const placed = new Set<number>();
  for (const row of data.rows) {
    if (row.sequence !== null) placed.add(row.sequence);
  }
  const others = [...placed].filter((sequence) => !walked.includes(sequence)).sort(ascending);

  const groups: Group[] = [...walked, ...others].map((sequence) => ({
    kind: 'sequence',
    sequence,
    rows: data.rows.filter((row) => row.sequence === sequence),
  }));
  const notPlaced = data.rows.filter((row) => row.sequence === null);
  if (notPlaced.length > 0) groups.push({ kind: 'not-placed', rows: notPlaced });
  return groups;
}

function walkerLine(sequence: number, data: PageOrderResponse): string {
  const paidSocial = data.paidSocialSequence === sequence;
  const cto = data.ctoSequence === sequence;
  if (paidSocial && cto) return 'Paid Social and CTO visitors walk this sequence';
  if (paidSocial) return 'Paid Social visitors walk this sequence';
  if (cto) return 'CTO visitors walk this sequence';
  return 'No visitors walk this sequence right now';
}

interface PageRowProps {
  row: PageOrderRow;
  first: boolean;
  walkOrder: PageOrderResponse['walkOrder'];
  /** Sequence groups carry the Pin column; Not placed rows have no pin. */
  inSequence: boolean;
  pinDisabled: boolean;
  onPin(row: PageOrderRow): void;
  onUnpin(row: PageOrderRow): void;
}

function PageRow({ row, first, walkOrder, inSequence, pinDisabled, onPin, onUnpin }: PageRowProps) {
  const nullSpotInSequence = row.sequence !== null && row.spot === null;
  const isNew =
    walkOrder === 'revenue' && row.spot !== null && (row.rank === null || !row.rank.ranked);
  return (
    <TableRow>
      <TableCell className="tabular-nums">{row.position ?? '—'}</TableCell>
      <TableCell className="tabular-nums">
        {row.spot !== null ? row.spot : nullSpotInSequence ? 'No spot' : '—'}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/campaigns/${row.campaignId}`}
            className="font-semibold text-ink hover:text-accent transition-colors"
          >
            {row.campaignName}
          </Link>
          {first && <Tag tone="accent">#1 today</Tag>}
          {row.pinned && <Tag tone="violet">Pinned</Tag>}
          {isNew && <Tag tone="sky">New: holds spot {row.spot}</Tag>}
          {row.sequence !== null && row.priceMissing && (
            <Tag tone="amber">No price: ranks as $0</Tag>
          )}
          {row.campaignStyle != null && row.campaignStyle !== 'custom_optin' && (
            <span className="text-xs text-muted-foreground">
              {campaignStyleEnumToLabel(row.campaignStyle)}
            </span>
          )}
          {row.archived && <Tag>Archived</Tag>}
        </div>
        {nullSpotInSequence && (
          <p className="mt-1 text-xs text-muted-foreground">{NULL_SPOT_HINT}</p>
        )}
      </TableCell>
      <TableCell>
        <StatusPill value={row.status} />
      </TableCell>
      <TableCell>{fmtDate(row.startDate) || '—'}</TableCell>
      <TableCell>{fmtDate(row.endDate) || '—'}</TableCell>
      <TableCell>{row.liveToday ? 'Yes' : 'No'}</TableCell>
      <TableCell className="tabular-nums">
        {row.rank === null ? '—' : fmtCount(row.rank.responses)}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.rank === null ? '—' : fmtMoney(row.rank.revenue)}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.rank === null || row.rank.revenuePerResponse === null ? (
          '—'
        ) : (
          <span title={`$${row.rank.revenuePerResponse.toFixed(4)}`}>
            {fmtMoney(row.rank.revenuePerResponse)}
          </span>
        )}
      </TableCell>
      <TableCell>
        {inSequence && row.spot !== null && (
          <Button
            type="button"
            size="sm"
            disabled={pinDisabled}
            onClick={() => (row.pinned ? onUnpin(row) : onPin(row))}
          >
            {row.pinned ? 'Unpin' : 'Pin to #1'}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

interface GroupCardProps {
  group: Group;
  data: PageOrderResponse;
  /** The sequence whose editor is open, if any (one card edits at a time). */
  editingSequence: number | null;
  /** A pin request is in flight: every pin button waits. */
  pinBusy: boolean;
  onPin(row: PageOrderRow): void;
  onUnpin(row: PageOrderRow): void;
  onEdit(sequence: number): void;
  onSaved(data: PageOrderResponse): void;
  onStale(): void;
  onCancel(): void;
}

function GroupCard({
  group,
  data,
  editingSequence,
  pinBusy,
  onPin,
  onUnpin,
  onEdit,
  onSaved,
  onStale,
  onCancel,
}: GroupCardProps) {
  const revenueMode = data.walkOrder === 'revenue';
  const rows = group.rows;
  // R3: in revenue mode the API rows are in walk order; the editor starts from the fallback (spot) order.
  const editorRows = useMemo(
    () => (revenueMode ? [...rows].sort(bySpot) : rows),
    [revenueMode, rows]
  );
  const isSequence = group.kind === 'sequence';
  const editing = isSequence && group.sequence === editingSequence;
  const canReorder = isSequence && rows.some((row) => !isEndedPage(row));
  const walked =
    isSequence &&
    (group.sequence === data.paidSocialSequence || group.sequence === data.ctoSequence);
  const liveRows = rows.filter((row) => row.liveToday);
  // The walk shows the first live row in API order (spot or not) first; Not
  // placed pages are never in a walk.
  const firstLiveId = isSequence ? liveRows[0]?.campaignId : undefined;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>{isSequence ? `Sequence ${group.sequence}` : 'Not placed'}</CardTitle>
            {isSequence && (
              <p className="text-sm text-muted-foreground">{walkerLine(group.sequence, data)}</p>
            )}
            {isSequence && revenueMode && (
              <p className="text-sm text-muted-foreground">
                {data.ranking === null
                  ? 'Ordered by earnings once the first nightly ranking runs; until then pages show by spot.'
                  : `Ordered by earnings per response, refreshed nightly. Ranked on ${fmtDate(data.ranking.rankedOn)} from ${fmtDate(data.ranking.windowFrom)} to ${fmtDate(data.ranking.windowTo)}.`}
              </p>
            )}
          </div>
          {canReorder && !editing && (
            <Button
              type="button"
              size="sm"
              disabled={editingSequence !== null}
              onClick={() => onEdit(group.sequence)}
            >
              {revenueMode ? 'Change fallback order' : 'Change order'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isSequence && revenueMode && data.ranking !== null && isStale(data.ranking.rankedOn) && (
          <Banner tone="warn" className="mb-4">
            The nightly ranking hasn&apos;t run since {fmtDate(data.ranking.rankedOn)}, so this
            order uses older numbers.
          </Banner>
        )}
        {walked && liveRows.length === 0 && (
          <Banner tone="warn" className="mb-4">
            No page in this sequence is in the walk today, so its visitors see no custom page and go
            straight to the end of the walk.
          </Banner>
        )}
        {editing ? (
          <>
            {revenueMode && <p className="mb-3 text-sm text-muted-foreground">{REVENUE_HELP}</p>}
            <SequenceReorder
              sequence={group.sequence}
              rows={editorRows}
              onSaved={onSaved}
              onStale={onStale}
              onCancel={onCancel}
            />
          </>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Spot</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>In the walk today</TableHead>
                <TableHead>Responses (7 d)</TableHead>
                <TableHead>Revenue (7 d)</TableHead>
                <TableHead>Revenue / response</TableHead>
                <TableHead>Pin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                    No pages in this sequence.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <PageRow
                    key={row.campaignId}
                    row={row}
                    first={row.campaignId === firstLiveId}
                    walkOrder={data.walkOrder}
                    inSequence={isSequence}
                    pinDisabled={pinBusy || editingSequence !== null}
                    onPin={onPin}
                    onUnpin={onUnpin}
                  />
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export function PageOrder() {
  const [data, setData] = useState<PageOrderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingSequence, setEditingSequence] = useState<number | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(() => {
    api
      .getPageOrder()
      .then(setData)
      .catch((err: ApiError) => setError(err.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function changePin(row: PageOrderRow, pin: boolean) {
    const { sequence } = row;
    if (data === null || sequence === null) return;
    setPinBusy(true);
    try {
      if (pin) {
        const replaced = data.rows.find(
          (other) =>
            other.sequence === sequence && other.pinned && other.campaignId !== row.campaignId
        );
        setData(await api.setPagePin(sequence, row.campaignId));
        toast.success(
          `Pinned "${row.campaignName}" to #1 in Sequence ${sequence}` +
            (replaced ? ` (replaces "${replaced.campaignName}")` : '')
        );
      } else {
        setData(await api.clearPagePin(sequence));
        toast.success(`Unpinned "${row.campaignName}"`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change the pin');
    } finally {
      setPinBusy(false);
    }
  }

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading page order: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Page Order</h1>
        <p className="text-muted-foreground mt-2">
          Every custom page by sequence and spot. A page is in the walk today when it is Active or
          Test and today is within its dates. A visitor can still skip it if their email is on its
          suppression list or its pre-ping declines. Use Change order on a sequence to put its pages
          in order. To move a page to another sequence, or to place a new page, use its campaign
          form.
        </p>
        <p className="text-muted-foreground mt-2">
          In each sequence your pinned page shows first. The other pages follow by what each earned
          per response over the last 7 days. A response is a visitor clicking Accept or Skip on the
          page, and the ranking refreshes every night around 4 AM ET. A page with fewer than 100
          responses in those 7 days is new: it keeps the place its spot number gives it until it has
          enough.
        </p>
      </div>

      {data === null ? (
        <p className="text-center text-muted-foreground py-8">Loading…</p>
      ) : (
        <>
          {!data.walkEnabled && (
            <Banner tone="stop" className="mb-6">
              The offer walk is switched off (OFFERS_WALK_ENABLED), so no visitor sees any custom
              page until it&apos;s on.
            </Banner>
          )}
          {data.walkOrder === 'spot' && (
            <Banner tone="warn" className="mb-6">
              {SPOT_MODE_BANNER}
            </Banner>
          )}
          {buildGroups(data).map((group) => (
            <GroupCard
              key={group.kind === 'sequence' ? `sequence-${group.sequence}` : 'not-placed'}
              group={group}
              data={data}
              editingSequence={editingSequence}
              pinBusy={pinBusy}
              onPin={(row) => void changePin(row, true)}
              onUnpin={(row) => void changePin(row, false)}
              onEdit={setEditingSequence}
              onSaved={(saved) => {
                setData(saved);
                setEditingSequence(null);
              }}
              onStale={() => {
                setEditingSequence(null);
                load();
              }}
              onCancel={() => setEditingSequence(null)}
            />
          ))}
        </>
      )}
    </div>
  );
}
